// @flow

import Notifications from '../constants/Notifications';
import AsyncRequest from '../classes/AsyncRequest';
import PopupModals from '../constants/PopupModals';
import User from '../classes/User';
import EventNames from '../constants/DataLayer/EventNames';
import { Component } from '../jsti/core';
import Cookies from '../constants/Cookies';
import Cookie from '../classes/Cookie';
import { data } from 'autoprefixer';
import type Product from '../classes/Product';

export default class CartNotification extends Component {
  #maximumOrderPrice: ?number;
  #minimumOrderPrice: ?number;
  #cartItems: Product[];

  /**
   * Initialize the component.
   *
   * @param pEl
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);

    this.#maximumOrderPrice = +this.el.dataset.maximumOrderPrice || null;
    this.#minimumOrderPrice = +this.el.dataset.minimumOrderPrice || null;

    this.addListener(Notifications.CART_ADD_ITEM, this.add);
    this.addListener(Notifications.CART_UPDATE_ITEM, this.update);
    this.addListener(Notifications.CART_REMOVE_ITEM, this.remove);
    this.addListener(Notifications.TOGGLE_MODAL, this.onToggleModal);
    this.addListener(Notifications.CART_UPDATE_DATA, (data: Object) => {
      this.#cartItems = data.items;
      this.updateInsiderBasket(data);
    });

    if (this.el.dataset.cartUnknown) {
      this.getCart();
    } else {
      this.#cartItems = JSON.parse(this.el.dataset.cartItems);
    }
  }

  /**
   * Get cart data from our API
   */
  getCart(): void {
    AsyncRequest.post<Object>('cart', {
      preferred_shipping_country: Cookie.get(Cookies.PREFERRED_SHIPPING_COUNTRY)
    }, '/cart')
      .then(({ data: { data } }: Object) => {
        if (!data) {
          throw new Error('Something went wrong');
        }

        this.notify(Notifications.CART_SHOW_TOTAL_COUNTER);

        this.el.dataset.dataLayer = JSON.stringify(data.dataLayer?.product || []);

        if (data.count) {
          this.notify(Notifications.HIDE_EMPTY_CART);
          this.notify(Notifications.CART_UPDATE_DATA, data);
        } else {
          this.notify(Notifications.SHOW_EMPTY_CART);
        }

        this.#cartItems = data.items;
        if (data.summary) {
          this.notifyOrderRestrictionInformation(+data.summary.total, +data.summary.subtotal);
        }
      })
      .catch((error: Error) => {
        this.notify(Notifications.CART_SHOW_TOTAL_COUNTER);
        console.error(error);
      });
  }

  /**
   * Send a async request to our backend so the API can add the item to the cart.
   */
  add({
    sku,
    amount,
    openNotification,
    shouldDispatchDataLayer,
    subscriptionPeriod,
    crossSell = false,
    openCrossSell = true,
    location
  }: Object): void {
    AsyncRequest.post('', { sku, amount, subscriptionPeriod }, '/add-to-cart')
      .then(({ data }: Object) => {
        if (!data) {
          throw new Error('Something went wrong');
        }

        this.notify(Notifications.SHOW_CART_NOTIFICATION, { show: true });
        const addedItem = data.items.find((product: Product) => product.sku === sku);
        setTimeout(() => {
          this.notify(Notifications.CART_UPDATE_DATA, data);
          this.notify(Notifications.HIDE_EMPTY_CART);
          this.notifyOrderRestrictionInformation(+data.summary.total, +data.summary.subtotal);

          if (openNotification) {
            if (crossSell && openCrossSell) {
              this.notify(Notifications.CROSS_SELL_UPDATE_DATA, { addedItem, amount });
              this.notify(Notifications.TOGGLE_MODAL, { id: PopupModals.CROSS_SELL_NOTIFICATION });
              this.notify(Notifications.TOGGLE_MODAL, { id: PopupModals.CROSS_SELL_MODAL });
            } else {
              this.notify(Notifications.TOGGLE_MODAL, { id: PopupModals.CART_NOTIFICATION });
            }
          }

          if (shouldDispatchDataLayer) {
            this.dispatch({
              event: EventNames.ADD_TO_CART,
              data: {
                sku: addedItem.sku,
                title: addedItem.title,
                item_id: addedItem.parent_sku,
                item_name: addedItem.parent_title,
                category: addedItem.category,
                brand: addedItem.brand,
                price: addedItem.price_ex_vat,
                quantity: amount,
                subscription_selection: addedItem.subscription?.frequency.id ?? null,
                subscription_period: addedItem.subscription?.period ?? null,
                location: location
              },
              GA4: true
            });
          }
        }, 100);
      })
      .catch(() => {
        User.notifyFail();
        this.notify(Notifications.ASYNC_FAILED);
      });
  }

  /**
   * Send a async request to our backend so the API can update the item in the cart.
   */
  update({ sku, amount, difference, subscriptionPeriod, location }: Object): void {
    AsyncRequest.post('', { sku, amount, subscriptionPeriod }, '/update-cart')
      .then(({ data }: Object) => {
        if (!data) {
          throw new Error('Something went wrong');
        }

        this.notify(Notifications.CART_UPDATE_DATA, data);
        this.notifyOrderRestrictionInformation(+data.summary.total, +data.summary.subtotal);
        if (!difference) return;
        this.sendEventOnUpdate(sku, difference, data, location);
      })
      .catch(() => {
        User.notifyFail();
      });
  }

  /**
   * Send a async request to our backend so the API can delete an item from the cart.
   */
  remove({ sku, amount }: Object): void {
    AsyncRequest.post('', { sku }, '/remove-from-cart')
      .then(({ data: { data } }: Object) => {
        if (!data) {
          throw new Error('Something went wrong');
        }

        this.notify(Notifications.CART_UPDATE_DATA, data);
        this.notifyOrderRestrictionInformation(+data.summary.total, +data.summary.subtotal);
        if (!data.count) {
          this.notify(Notifications.SHOW_EMPTY_CART);
        }
      })
      .catch(() => {
        User.notifyFail();
      });

    const removedItem = this.findProductInCartItems(sku);
    this.dispatch({
      event: EventNames.REMOVE_FROM_CART,
      data: {
        products: [{
          sku: removedItem.sku,
          title: removedItem.title,
          item_id: removedItem.parent_sku,
          item_name: removedItem.parent_title,
          category: removedItem.category,
          brand: removedItem.brand,
          price: removedItem.price_ex_vat,
          quantity: amount,
          subscription_selection: removedItem.subscription?.frequency.id ?? null,
          subscription_period: removedItem.subscription?.period ?? null
        }]
      },
      GA4: true
    });
  }

  /**
   * Toggle the use of the page (scrollbar) when the modal is toggled.
   */
  onToggleModal(pData: Object): void {
    // TODO: This is a new feature of the popup modal, so we can delete this and use it as a configuration.
    if (pData.id === PopupModals.CART_NOTIFICATION) {
      this.notify(Notifications.TOGGLE_SCROLLBAR_STATE);
    }
  }

  /**
   * Dispatch an add to cart or remove from cart event
   */
  sendEventOnUpdate(sku: string, difference: number, cart: Object, location: string): void {
    if (difference < 0) {
      const removedItem = this.findProductInCartItems(sku);
      this.dispatch({
        event: EventNames.REMOVE_FROM_CART,
        data: {
          products: [{
            sku: removedItem.sku,
            title: removedItem.title,
            item_id: removedItem.parent_sku,
            item_name: removedItem.parent_title,
            category: removedItem.category,
            brand: removedItem.brand,
            price: removedItem.price_ex_vat,
            quantity: Math.abs(difference),
            subscription_selection: removedItem.subscription?.frequency.id ?? null,
            subscription_period: removedItem.subscription?.period ?? null
          }]
        },
        GA4: true
      });
    } else if (difference > 0) {
      const addedItem = cart.items.find((product: Product) => product.sku === sku);
      this.dispatch({
        event: EventNames.ADD_TO_CART,
        data: {
          sku: addedItem.sku,
          title: addedItem.title,
          item_id: addedItem.parent_sku,
          item_name: addedItem.parent_title,
          category: addedItem.category,
          brand: addedItem.brand,
          price: addedItem.price_ex_vat,
          quantity: difference,
          subscription_selection: addedItem.subscription?.frequency.id ?? null,
          subscription_period: addedItem.subscription?.period ?? null,
          location: location
        },
        GA4: true
      });
    }
  }

  /**
   * Notify other components information about the order restrictions
   */
  notifyOrderRestrictionInformation(orderTotal: number, subTotal: number): void {
    if (this.#maximumOrderPrice) {
      if (orderTotal > this.#maximumOrderPrice) {
        this.notify(Notifications.EXCEEDED_MAXIMUM_ORDER_PRICE);
      } else {
        this.notify(Notifications.MAXIMUM_ORDER_ALLOWED);
      }
    }

    if (this.#minimumOrderPrice) {
      if (subTotal > this.#minimumOrderPrice) {
        this.notify(Notifications.MINIMUM_ORDER_ALLOWED);
      } else {
        this.notify(Notifications.MINIMUM_ORDER_PRICE_REACHED);
      }
    }
  }

  /**
   * Find a product with the given sku in the datalayer
   */
  findProductInDataLayer(sku: string): ?Object {
    let product: ?Object = window.dataLayer[0].products?.find((product: Object) => product.variant === sku);

    if (product) return product;
    const impressionVariants = window.dataLayer
      .filter((event: Object) => event.event === 'productImpression')
      .map((event: Object) => {
        return event.ecommerce.impressions.find((impression: Object) => {
          return impression.variant === sku;
        });
      })
      .filter(Boolean);

    if (impressionVariants.length) {
      product = impressionVariants[0];
    }

    if (product) return product;
    const addToCartProducts = window.dataLayer
      .filter((event: Object) => event.event === 'addToCart')
      .map((event: Object) => {
        return event.ecommerce.add.products.find((product: Object) => {
          return product.variant === sku;
        });
      })
      .filter(Boolean);

    if (addToCartProducts.length) {
      product = addToCartProducts[0];
    }

    return product;
  }

  /**
   * Find a product with the given sku in the datalayer dataset of the component
   */
  findProductInDataset(sku: string): ?Object {
    try {
      return JSON.parse(this.el.dataset.dataLayer).find((product: Object) => product.variant === sku);
    } catch {
      return null;
    }
  }

  /**
   * Find a product with the given sku in the cart items
   */
  findProductInCartItems(sku: string): ?Object {
    try {
      return this.#cartItems.find((product: Object) => product.sku === sku);
    } catch {
      return null;
    }
  }

  /**
   *
   */
  updateInsiderBasket(data: Object) {
    const productsInsider = data.items.map((product: Object) => {
      return {
        product: {
          id: product.sku,
          name: product.title,
          taxonomy: product.category.split('/'),
          url: window.location.origin + '/' + product.slug,
          currency: window.currency,
          unit_price: +product.price_original,
          unit_sale_price: +product.price,
          product_image_url: product.image?.src ? window.store.cdn.url + product.image.src : '',
          custom: product.custom
        },
        subtotal: +product.total,
        quantity: product.amount
      };
    });

    this.dispatch({
      event: EventNames.SET_INSIDER_BASKET,
      Insider: true,
      data: {
        basket: {
          currency: window.currency,
          line_items: productsInsider.length === 0 ? null : productsInsider,
          total: productsInsider.length === 0 ? 0 : +data.total
        }
      }
    });
  }
}
